import { useAuth } from '../contexts/AuthContext';
import { ROLE_PERMISSIONS, type RolePermissions, type UserRole } from '../types';

/**
 * Hook to get user permissions based on their role
 */
export function usePermissions(): RolePermissions {
  const { user } = useAuth();
  
  if (!user) {
    // Return all false permissions for unauthenticated users
    return {
      canViewClients: false,
      canCreateClients: false,
      canEditClients: false,
      canDeleteClients: false,
      canViewTreatments: false,
      canCreateTreatments: false,
      canEditTreatments: false,
      canDeleteTreatments: false,
      canViewAppointments: false,
      canCreateAppointments: false,
      canEditAppointments: false,
      canDeleteAppointments: false,
      canViewReports: false,
      canExportReports: false,
      canViewUsers: false,
      canCreateUsers: false,
      canEditUsers: false,
      canDeleteUsers: false,
      canManageRoles: false,
      canViewSettings: false,
      canEditSettings: false,
      canManageLocations: false,
      canManageSubscription: false,
      canViewFinancials: false,
      canManageBilling: false,
      canAccessSystemLogs: false,
      canManageIntegrations: false,
    };
  }
  
  return ROLE_PERMISSIONS[user.role];
}

/**
 * Hook to check if user has a specific permission
 */
export function usePermission(permission: keyof RolePermissions): boolean {
  const permissions = usePermissions();
  return permissions[permission];
}

/**
 * Hook to check if user has any of the specified permissions
 */
export function useAnyPermission(permissions: (keyof RolePermissions)[]): boolean {
  const userPermissions = usePermissions();
  return permissions.some(permission => userPermissions[permission]);
}

/**
 * Hook to check if user has all of the specified permissions
 */
export function useAllPermissions(permissions: (keyof RolePermissions)[]): boolean {
  const userPermissions = usePermissions();
  return permissions.every(permission => userPermissions[permission]);
}

/**
 * Hook to check if user has a specific role
 */
export function useRole(role: UserRole): boolean {
  const { user } = useAuth();
  return user?.role === role;
}

/**
 * Hook to check if user has any of the specified roles
 */
export function useAnyRole(roles: UserRole[]): boolean {
  const { user } = useAuth();
  return user ? roles.includes(user.role) : false;
}

/**
 * Hook to check if user has a role higher than or equal to the specified role
 * Role hierarchy: admin > manager > therapist > receptionist
 */
export function useRoleOrHigher(role: UserRole): boolean {
  const { user } = useAuth();
  
  if (!user) return false;
  
  const roleHierarchy: Record<UserRole, number> = {
    admin: 4,
    manager: 3,
    therapist: 2,
    receptionist: 1,
  };
  
  return roleHierarchy[user.role] >= roleHierarchy[role];
}
